import React, { useState } from 'react';
import styled from 'styled-components';
import { X, Plus } from 'lucide-react';

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(6px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  animation: fadeIn 0.2s ease;
  overflow-y: auto;
  padding: 20px;

  @keyframes fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }
`;

const ModalContent = styled.div`
  background: var(--card-bg);
  border-radius: 12px;
  width: 95%;
  max-width: 800px;
  display: flex;
  flex-direction: column;
  box-shadow: 0 8px 32px rgba(165, 145, 239, 0.6);
  border: 1px solid var(--card-border);
  animation: slideUp 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
  overflow: hidden;
  position: relative;
  max-height: 90vh;

  @keyframes slideUp {
    from { 
      opacity: 0;
      transform: translateY(20px) scale(0.95);
    }
    to { 
      opacity: 1;
      transform: translateY(0) scale(1);
    }
  }
`;

const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 24px;
  background: var(--bg);
  border-bottom: 1px solid var(--card-border);
`;

const ModalTitle = styled.h2`
  font-size: 20px;
  font-weight: 700;
  color: var(--text-primary);
  margin: 0;
  display: flex;
  align-items: center;
  gap: 12px;
  
  svg {
    color: var(--primary);
  }
`;

const CloseButton = styled.button`
  width: 36px;
  height: 36px;
  border-radius: 8px;
  background: transparent;
  border: none;
  color: var(--text-secondary);
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background: var(--error);
    color: white;
    transform: scale(1.1);
  }
`;

const ModalBody = styled.div`
  padding: 24px;
  background: var(--card-bg);
  overflow-y: auto;
  max-height: calc(90vh - 140px);
`;

const FormSection = styled.div`
  margin-bottom: 24px;
`;

const SectionTitle = styled.h3`
  font-size: 12px;
  font-weight: 700;
  color: var(--text-secondary);
  margin: 0 0 16px 0;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const FormGroup = styled.div`
  margin-bottom: 16px;
`;



const Select = styled.select`
  width: 100%;
  background: var(--bg);
  border: 1px solid var(--card-border);
  border-radius: 8px;
  padding: 12px 16px;
  color: var(--text-primary);
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s ease;
  font-weight: 500;

  &:focus {
    outline: none;
    border-color: var(--primary);
    box-shadow: 0 0 0 3px rgba(165, 145, 239, 0.1);
  }

  &:hover {
    border-color: var(--primary);
  }

  option {
    background: var(--bg);
    color: var(--text-primary);
    padding: 12px;
  }
`;

const DeviceGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 12px;
`;

const DeviceOption = styled.div`
  background: ${props => props.$selected ? 'rgba(165, 145, 239, 0.15)' : 'var(--bg)'};
  border: 2px solid ${props => props.$selected ? 'var(--primary)' : 'var(--card-border)'};
  border-radius: 8px;
  padding: 12px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: ${props => props.$selected ? 'rgba(165, 145, 239, 0.2)' : 'rgba(165, 145, 239, 0.1)'};
    border-color: var(--primary);
    transform: translateY(-2px);
  }
`;

const DeviceOptionHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
`;

const DeviceAvatar = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: linear-gradient(135deg, var(--primary), var(--secondary));
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--text-primary);
  font-weight: 700;
  font-size: 14px;
`;

const DeviceUsername = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: var(--text-primary);
  flex: 1;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const DeviceName = styled.div`
  font-size: 11px;
  color: #888;
  margin-left: 40px;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  padding: 20px 24px;
  background: var(--bg);
  border-top: 1px solid var(--card-border);
`;

const Button = styled.button`
  background: ${props => props.$primary 
    ? 'linear-gradient(135deg, var(--primary), var(--secondary))' 
    : 'transparent'};
  border: ${props => props.$primary ? 'none' : '1px solid var(--card-border)'};
  border-radius: 8px;
  padding: 12px 24px;
  color: var(--text-primary);
  font-weight: 600;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 8px;

  &:hover {
    background: ${props => props.$primary 
      ? 'linear-gradient(135deg, var(--secondary), var(--secondary))' 
      : 'rgba(255, 255, 255, 0.05)'};
    transform: translateY(-1px);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }
`;

const AddToQueueModal = ({ workflow, onClose, onAddToQueue }) => {
  const [scriptType, setScriptType] = useState('interact');
  const [selectedDevices, setSelectedDevices] = useState([]);

  const scriptTypes = [
    { value: 'interact', label: 'Interaction (Like, Follow, Comment)' },
    { value: 'unfollow', label: 'Unfollow' },
    { value: 'gmail', label: 'Gmail Check' },
    { value: 'follow', label: 'Follow Only' },
    { value: 'like', label: 'Like Only' }
  ];

  const toggleDevice = (deviceId) => {
    setSelectedDevices(prev => 
      prev.includes(deviceId) 
        ? prev.filter(id => id !== deviceId)
        : [...prev, deviceId]
    );
  };

  const handleSubmit = () => {
    if (selectedDevices.length === 0) {
      alert('Veuillez sélectionner au moins un device');
      return;
    }

    const queueItems = selectedDevices.map(deviceId => {
      const device = workflow.devices.find(d => d.id === deviceId);
      return {
        workflowId: workflow.id,
        deviceId,
        accountId: device.account_id,
        deviceName: device.name,
        deviceUsername: device.username,
        scriptType,
        status: 'pending'
      };
    });

    onAddToQueue(queueItems);
    onClose();
  };

  return (
    <ModalOverlay onClick={onClose}>
      <ModalContent onClick={(e) => e.stopPropagation()}>
        <ModalHeader>
          <ModalTitle>
            <Plus size={24} />
            Ajouter à la file d'exécution
          </ModalTitle>
          <CloseButton onClick={onClose}>
            <X size={24} />
          </CloseButton>
        </ModalHeader>

        <ModalBody>
          <FormSection>
            <SectionTitle>Type de script</SectionTitle>
            <FormGroup>
              <Select value={scriptType} onChange={(e) => setScriptType(e.target.value)}>
                {scriptTypes.map(type => (
                  <option key={type.value} value={type.value}>
                    {type.label}
                  </option>
                ))}
              </Select>
            </FormGroup>
          </FormSection>

          <FormSection>
            <SectionTitle>Sélectionner les devices</SectionTitle>
            <DeviceGrid>
              {workflow.devices.map(device => (
                <DeviceOption
                  key={device.id}
                  $selected={selectedDevices.includes(device.id)}
                  onClick={() => toggleDevice(device.id)}
                >
                  <DeviceOptionHeader>
                    <DeviceAvatar>
                      {device.username ? device.username.charAt(0).toUpperCase() : '?'}
                    </DeviceAvatar>
                    <DeviceUsername>@{device.username || 'N/A'}</DeviceUsername>
                  </DeviceOptionHeader>
                  <DeviceName>{device.name}</DeviceName>
                </DeviceOption>
              ))}
            </DeviceGrid>
          </FormSection>
        </ModalBody>

        <ButtonGroup>
          <Button onClick={onClose}>Annuler</Button>
          <Button 
            $primary 
            onClick={handleSubmit}
            disabled={selectedDevices.length === 0}
          >
            <Plus size={18} />
            Ajouter à la queue ({selectedDevices.length})
          </Button>
        </ButtonGroup>
      </ModalContent>
    </ModalOverlay>
  );
};

export default AddToQueueModal;
